// src/components/microphone/GlobalAudioWrapper.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { FaChevronUp, FaChevronDown, FaBars } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import DirectAudioPlayer from './DirectAudioPlayer';
import './GlobalAudioWrapper.css';

const GlobalAudioWrapper = () => {
  const { microphones, audioStreamData } = useContext(AppContext);
  const [activeMicId, setActiveMicId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const savedCollapsed = localStorage.getItem('audioPlayer_collapsed');
    return savedCollapsed === 'true';
  });
  const [position, setPosition] = useState(() => {
    // Load position from localStorage
    try {
      const savedPosition = localStorage.getItem('audioPlayer_position');
      return savedPosition ? JSON.parse(savedPosition) : { x: null, y: null };
    } catch (e) {
      return { x: null, y: null };
    }
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  const wrapperRef = useRef(null);
  
  // Auto-select first microphone if none is selected
  useEffect(() => {
    if (!activeMicId && microphones.length > 0) {
      setActiveMicId(microphones[0].id);
      console.log(`Auto-selected microphone: ${microphones[0].id}`);
    }
  }, [microphones, activeMicId]);
  
  // Get active microphone data
  const activeAudioData = activeMicId ? audioStreamData[activeMicId] : null;
  
  // Get the active microphone name
  const activeMicName = activeMicId 
    ? microphones.find(mic => mic.id === activeMicId)?.name || activeMicId
    : 'None';
  
  // Handle microphone selection change
  const handleMicChange = (event) => {
    setActiveMicId(event.target.value);
  };
  
  // Toggle collapse state
  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    // Save to localStorage
    localStorage.setItem('audioPlayer_collapsed', newCollapsed);
  };
  
  // Dragging handlers
  const handleDragStart = (e) => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      
      // Handle both mouse and touch events
      const clientX = e.clientX || e.touches[0].clientX;
      const clientY = e.clientY || e.touches[0].clientY;
      
      setDragOffset({
        x: clientX - rect.left,
        y: clientY - rect.top
      });
      setIsDragging(true);
      
      // Prevent default behavior for touch events
      if (e.type === 'touchstart') {
        e.preventDefault();
      }
    }
  };
  
  const handleDragMove = (e) => {
    if (isDragging) {
      // Handle both mouse and touch events
      const clientX = e.clientX || e.touches[0].clientX;
      const clientY = e.clientY || e.touches[0].clientY;
      
      // Calculate new position
      const newX = clientX - dragOffset.x;
      const newY = clientY - dragOffset.y;
      
      // Keep player within window bounds
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const playerWidth = wrapperRef.current?.offsetWidth || 300;
      const playerHeight = wrapperRef.current?.offsetHeight || 200;
      
      const boundedX = Math.max(0, Math.min(newX, windowWidth - playerWidth));
      const boundedY = Math.max(0, Math.min(newY, windowHeight - playerHeight));
      
      setPosition({ x: boundedX, y: boundedY });
      
      // Prevent text selection and scrolling during drag
      e.preventDefault();
    }
  };
  
  const handleDragEnd = () => {
    setIsDragging(false);
    
    // Save position to localStorage if it has changed
    if (position.x !== null && position.y !== null) {
      localStorage.setItem('audioPlayer_position', JSON.stringify(position));
    }
  };
  
  // Mouse event handlers
  const handleMouseDown = (e) => handleDragStart(e);
  const handleMouseMove = (e) => handleDragMove(e);
  const handleMouseUp = () => handleDragEnd();
  
  // Touch event handlers
  const handleTouchStart = (e) => handleDragStart(e);
  const handleTouchMove = (e) => handleDragMove(e);
  const handleTouchEnd = () => handleDragEnd();
  
  // Add and remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      // Mouse events
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Touch events
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      document.addEventListener('touchcancel', handleTouchEnd);
    } else {
      // Mouse events
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Touch events
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    }
    
    return () => {
      // Mouse events
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Touch events
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging]);
  
  // Handle window resize - keep player within bounds
  useEffect(() => {
    const handleResize = () => {
      if (position.x !== null && position.y !== null) {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const playerWidth = wrapperRef.current?.offsetWidth || 300;
        const playerHeight = wrapperRef.current?.offsetHeight || 200;
        
        const boundedX = Math.max(0, Math.min(position.x, windowWidth - playerWidth));
        const boundedY = Math.max(0, Math.min(position.y, windowHeight - playerHeight));
        
        if (boundedX !== position.x || boundedY !== position.y) {
          const newPosition = { x: boundedX, y: boundedY };
          setPosition(newPosition);
          localStorage.setItem('audioPlayer_position', JSON.stringify(newPosition));
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [position]);
  
  // Determine style based on position
  const wrapperStyle = position.x !== null && position.y !== null
    ? { 
        top: `${position.y}px`, 
        left: `${position.x}px`,
        right: 'auto',
        bottom: 'auto',
        position: 'fixed'
      }
    : {};
  
  return (
    <div 
      ref={wrapperRef}
      className={`global-audio-wrapper ${collapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''}`}
      style={wrapperStyle}
    >
      <div className="global-audio-header">
        <div 
          className="drag-handle" 
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <FaBars />
        </div>
        
        <div className="header-content">
          {!collapsed && (
            <select 
              value={activeMicId || ''} 
              onChange={handleMicChange}
              className="mic-selector"
            >
              <option value="">Select microphone</option>
              {microphones.map(mic => (
                <option key={mic.id} value={mic.id}>
                  {mic.name} ({mic.location || 'Unknown'})
                </option>
              ))}
            </select>
          )}
          
          {!collapsed && (
            <div className="active-mic-label">
              Active Microphone: <span className="active-mic-name">{activeMicName}</span>
            </div>
          )}
          
          {collapsed && (
            <div className="collapsed-title">
              <span className="active-mic-name">{activeMicName}</span> - Audio Player
            </div>
          )}
        </div>
        
        <button 
          className="collapse-button" 
          onClick={toggleCollapse}
          aria-label={collapsed ? "Expand" : "Collapse"}
        >
          {collapsed ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>
      
      {!collapsed && (
        <div className="global-audio-player-container">
          <DirectAudioPlayer audioData={activeAudioData} />
        </div>
      )}
    </div>
  );
};

export default GlobalAudioWrapper;